<template>
  <div class="view-sales-subscriptions">
    <page-header
      title="Gestão de assinaturas"
      subtitle="Visualização e gerenciamento de suas assinaturas"
    >
      <bp-button
        variant="glyph"
        trailing-icon="fa fa-arrow-right"
        label="Ir para Vendas de assinaturas"
        @click="$router.push({ name: 'sales-subscriptions' })"
      />
    </page-header>

    <page-container>
      <div class="row">
        <div class="col-md mb-5 mb-lg-0">
          <div class="ticket-amount">
            <div class="flex-fill">
              <div class="d-flex flex-row">
                <span class="mr-3">Ativas</span>
                <span class="active-badge px-3">
                  {{ Math.round(overview.total_enable.tax) || 0 }}%
                </span>
              </div>
            </div>
            <span>{{ overview.total_enable.count || 0 }}</span>
          </div>
        </div>
        <div class="col-md mb-5 mb-lg-0">
          <div class="ticket-amount">
            <div class="flex-fill">
              <div class="d-flex flex-row">
                <span class="mr-3">Atrasadas</span>
                <span class="draft-badge px-3">
                  {{ Math.round(overview.total_past_due.tax) || 0 }}%
                </span>
              </div>
            </div>
            <span>{{ overview.total_past_due.count || 0 }}</span>
          </div>
        </div>
        <div class="col-md mb-5 mb-lg-0">
          <div class="ticket-amount">
            <div class="flex-fill">
              <div class="d-flex flex-row">
                <span class="mr-3">Canceladas</span>
                <span class="inactive-badge px-3">
                  {{ Math.round(overview.total_disable.tax) || 0 }}%
                </span>
              </div>
            </div>
            <span>{{ overview.total_disable.count || 0 }}</span>
          </div>
        </div>
      </div>

      <hr class="border-neutral-100 my-7" />

      <div class="row justify-content-between mb-5 mb-lg-6">
        <div class="col order-1 order-lg-0">
          <div class="d-flex align-items-center text-neutral-800">
            <span class="functional-1 mr-2">
              Total: {{ pagination.count || 0 }}
            </span>
            <bp-button
              size="md"
              color="neutral"
              variant="glyph"
              icon="fa-light fa-refresh"
              @click="refresh"
            />
          </div>
        </div>

        <div class="col-xl-5 col-lg-6">
          <div
            class="d-flex align-items-lg-center flex-column flex-lg-row mb-2 gap-3"
          >
            <div class="d-flex align-items-center gap-3 flex-fill">
              <bp-input
                v-model="search"
                size="md"
                placeholder="Nome, e-mail"
                :delay="300"
                @change="dispatch()"
              />
              <bp-button
                color="neutral"
                size="md"
                variant="filled-1"
                icon="fa-regular fa-filter"
                @click="filtersModal = true"
              />
            </div>

            <bp-export-button
              action="subscriptions/getCSV"
              filename="subscriptions"
              :filters="filters"
            />
          </div>
        </div>
      </div>

      <div class="my-9 text-center" v-if="loading">
        <b-spinner variant="primary" />
      </div>

      <bp-data-table
        v-else
        main="status"
        :columns="columns"
        :data="subscriptions"
      >
        <template #comprador="{item}">
          <div>
            <div class="functional-1">
              {{ item.name }}
            </div>
            <div class="functional-2 text-neutral-400">
              {{ item.shopper_email }}
            </div>
          </div>
        </template>

        <template #produto="{value,item}">
          <div>
            <div class="functional-1">
              {{ value.name }}
            </div>
            <div v-if="item.plan" class="functional-2 text-neutral-400">
              {{ item.plan.name }}
            </div>
          </div>
        </template>

        <template v-slot:ciclo-atual="{ value, item }">
          <div class="d-flex">
            {{ value }}
            <bp-tooltip
              v-if="
                !item.cycles || item.cycles == 'Renova até o cliente cancelar!'
              "
              text="Renova até o cliente cancelar"
            >
              <i class="fa-regular fa-infinity ml-2" />
            </bp-tooltip>
          </div>
        </template>

        <template #status="{value,item}">
          <div :class="formatStatusBadge(value)">
            {{ formatStatusName(value) }}
          </div>
          <div
            class="functional-2 text-neutral-400"
            v-if="value !== 'enable' && item.cancel_signature_at"
          >
            Desde
            {{ new Date(item.cancel_signature_at).toLocaleDateString() }}
          </div>
        </template>
      </bp-data-table>

      <div class="d-flex justify-content-end mt-5" v-if="subscriptions.length">
        <bp-paginator
          v-show="!loading"
          v-model="currentPage"
          :pagination="pagination"
          @change-page="dispatch"
        />
      </div>
    </page-container>

    <responsive-modal
      v-model="filtersModal"
      title="Filtrar"
      description="Selecione os filtros e aplique"
    >
      <subscription-filter
        v-model="filters"
        @close="filtersModal = false"
        @input="() => dispatch()"
      />
    </responsive-modal>
  </div>
</template>

<script>
import SubscriptionFilter from "@/components/sale/SubscriptionFilter.vue";
import {
  PERIODICITY_NAME_MAP,
  PERIODICITY_NUMBER_MAP,
  SALE_PAY_METHOD_NAME_MAP,
  SUBSCRIPTION_STATUS_BADGE_MAP,
  SUBSCRIPTION_STATUS_NAME_MAP
} from "@/constants/sales.js";
import { formatMoney } from "@/functions/helpers.js";
import get from "lodash/get";
import { mapState } from "vuex";

export default {
  components: {
    SubscriptionFilter
  },
  data() {
    return {
      loading: false,
      currentPage: 1,
      filtersModal: false,
      filters: {
        product_id: "",
        status: []
      },
      search: ""
    };
  },
  mounted() {
    this.dispatch();
    this.$store.dispatch("product/get", { limit: 1000 }).then();
  },
  computed: {
    ...mapState("subscriptions", ["subscriptions", "overview", "pagination"]),
    ...mapState("product", ["products"]),
    columns() {
      return [
        {
          name: `ID da assinatura`,
          field: "id",
          size: "0.6fr"
        },
        {
          name: "Comprador",
          field: "shopper_email"
        },
        {
          name: "Produto",
          field: "product"
        },
        {
          name: "Valor",
          field: "amount",
          transform: v => `R$ ${formatMoney(v)}`,
          size: "0.6fr"
        },
        {
          name: "Periodicidade",
          field: "frequency",
          size: "0.8fr"
        },
        {
          name: "Ciclo atual",
          field: "current_cycle",
          size: "0.6fr",
          transform: this.formatCurrentCycle
        },
        {
          name: "Pagamento",
          field: "payment_method",
          transform: v => SALE_PAY_METHOD_NAME_MAP[v]
        },
        {
          name: "Início",
          field: "created_at",
          transform: v => new Date(v).toLocaleDateString("pt-BR"),
          size: "0.6fr"
        },
        {
          name: "Status",
          field: "status"
        },
        {
          name: "",
          type: "menu",
          options: this.menuOptions
        }
      ];
    },
    menuOptions() {
      return [
        {
          label: "Bloquear acesso",
          icon: "fa-light fa-lock",
          dialog: {
            text: "Tem certeza que deseja bloquear o acesso?",
            variant: "negative",
            icon: "fa fa-hexagon-exclamation",
            secondaryAction: this.submitBlock,
            buttonText: "Cancelar",
            secondaryButtonText: "Confirmar"
          }
        },
        {
          label: "Cancelar assinatura",
          icon: "icon-x font-weight-bold",
          class: "text-negative-600",
          disabled: item => {
            const activeStatuses = ["enable", "started", "past_due"];
            return !activeStatuses.includes(item.status);
          },
          dialog: {
            text: `Deseja mesmo realizar o cancelamento da assinatura?`,
            variant: "negative",
            icon: "fa fa-hexagon-exclamation",
            secondaryAction: this.submitDelete,
            buttonText: "Cancelar",
            secondaryButtonText: "Confirmar"
          }
        }
      ];
    }
  },
  methods: {
    refresh() {
      this.search = "";
      this.dispatch();
    },
    catchHandler(error) {
      if (error.response?.data?.details) {
        this.$message.error(error.response.data.details);
      }
    },
    dispatch(offset = 0) {
      this.loading = true;

      if (!offset) this.currentPage = 1;

      const params = {
        status: this.filters.status,
        product_id: this.filters.product_id,
        search: this.search,
        offset
      };

      Promise.all([
        this.$store.dispatch("subscriptions/getOverview", params),
        this.$store.dispatch("subscriptions/get", params)
      ])
        .catch(this.catchHandler)
        .finally(() => (this.loading = false));
    },

    submitBlock(item) {
      const params = {
        email: item.shopper_email,
        product_id: item.product.id
      };
      this.$store
        .dispatch("membersStudents/disableSubscription", params)
        .then(() => {
          this.$message.success("Acesso bloqueado.");
          this.dispatch();
        })
        .catch(error => {
          this.$message.error(
            get(
              error,
              "response.data.details",
              "Erro ao realizar o bloqueio do acesso."
            )
          );
        });
    },
    submitDelete(item) {
      this.$store
        .dispatch("subscriptions/delete", item.id)
        .then(() => {
          this.$message.success("Assinatura cancelada.");
          this.dispatch();
        })
        .catch(error => {
          this.$message.error(
            get(
              error,
              "response.data.details",
              "Erro ao realizar cancelamento."
            )
          );
        });
    },

    formatPeriodicity(periodicity) {
      return PERIODICITY_NAME_MAP[periodicity];
    },
    formatPeriodicityNumber(periodicity) {
      return PERIODICITY_NUMBER_MAP[periodicity];
    },
    formatStatusName(status) {
      return SUBSCRIPTION_STATUS_NAME_MAP[status];
    },
    formatStatusBadge(status) {
      return SUBSCRIPTION_STATUS_BADGE_MAP[status];
    },
    formatCurrentCycle(_, item) {
      if (!item.current_cycle) return "-";

      let value = item.current_cycle + " de ";
      if (item.cycles && item.cycles != "Renova até o cliente cancelar!")
        value += item.cycles;
      return value;
    }
  }
};
</script>
